/**
 * How many trailing characters of a key stay legible. Enough to tell two of an
 * organization's keys apart in the list, short enough to be useless on its own.
 */
const VISIBLE_SUFFIX_LENGTH = 4;

const MASK_CHARACTER = "•";
const MASK_LENGTH = 8;

/**
 * Mask a key for display.
 *
 * The list endpoint returns the full plaintext key -- interlude keeps the whole
 * value on purpose and treats masking as a frontend concern
 * (`ApiKeyOut.key` in interlude's `openapi/schemas.py`). So every surface that
 * shows a stored key has to mask it here rather than assume the server already
 * did. The value still reaches the browser and is visible in devtools; masking
 * keeps it out of the page, which is what shoulder-surfing and screenshots see.
 *
 * A key shorter than the suffix is masked whole rather than partly revealed:
 * a short value is more of itself per character, and no real Kong credential is
 * that short anyway.
 */
export function maskApiKey(key: string): string {
  const mask = MASK_CHARACTER.repeat(MASK_LENGTH);
  if (key.length <= VISIBLE_SUFFIX_LENGTH) return mask;
  return `${mask}${key.slice(-VISIBLE_SUFFIX_LENGTH)}`;
}

/**
 * The trailing characters `maskApiKey` leaves legible, on their own.
 *
 * This is the only part of a key a reader can see in the list, so it is also
 * the only thing they can be asked to type back when a key has no name.
 */
export function apiKeySuffix(key: string): string {
  if (key.length <= VISIBLE_SUFFIX_LENGTH) return key;
  return key.slice(-VISIBLE_SUFFIX_LENGTH);
}

export { VISIBLE_SUFFIX_LENGTH };
